import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getChapterSourcePagesApi, type ChapterSourceIndex } from './api';
import { chapterPageHref, initialChapterPage, type ChapterPage } from './lib/chapterSourceNav';
import SourcePdfContent from './SourcePdfContent';

export default function ChapterSourceReader({ bookId, chapterId, requestedPage, explicitTitle }: {
  bookId: string; chapterId: string; requestedPage: number; explicitTitle: boolean;
}) {
  const navigate = useNavigate();
  const [index, setIndex] = useState<ChapterSourceIndex>();
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const [readingMode, setReadingMode] = useState<'pdf' | 'text'>();
  useEffect(() => {
    const controller = new AbortController();
    setError(undefined);
    const timer = window.setTimeout(() => { controller.abort(); setError('章节目录加载超时，请重试'); }, 30000);
    void getChapterSourcePagesApi(bookId, chapterId, controller.signal)
      .then(data => { if (!controller.signal.aborted) setIndex(data); })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : '章节目录加载失败'); })
      .finally(() => window.clearTimeout(timer));
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [bookId, chapterId, attempt]);
  const page = index && initialChapterPage(index.pages, requestedPage, explicitTitle);
  const position = index?.pages.findIndex(item => item.page === page) ?? -1;
  const current = index?.pages[position];
  useEffect(() => {
    if (current && current.page !== requestedPage) navigate(chapterPageHref(bookId, chapterId, current), { replace: true });
  }, [bookId, chapterId, current, requestedPage, navigate]);
  function go(item: ChapterPage | undefined) {
    if (!item) return;
    navigate(chapterPageHref(bookId, chapterId, item), { replace: true });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
  const previous = position > 0 ? index?.pages[position - 1] : undefined;
  const next = position >= 0 ? index?.pages[position + 1] : undefined;
  const navigation = (bottom = false) => <nav className={`chapter-page-nav${bottom ? ' chapter-page-nav-bottom' : ''}`} aria-label={bottom ? '页尾翻页' : '章节翻页'}>
    <button className="btn" disabled={!previous} onClick={() => go(previous)}>上一页</button>
    <span aria-live="polite">{current?.label} · PDF {page} 页</span>
    <button className="btn" disabled={!next} onClick={() => go(next)}>下一页</button>
  </nav>;
  return <main className="container source-page-wrap">
    <div className="source-page-head"><div><p className="eyebrow">章节阅读</p>
      <h1>{index?.title ?? '正在加载章节'} · 章节原文</h1>
      {index && <p>PDF {index.page_start}–{index.page_end} 页 · {index.pages.filter(item => item.kind === 'content').length} 个正文页</p>}</div>
      <Link className="btn" to={`/book/${encodeURIComponent(bookId)}`}>返回图书资料</Link></div>
    {error ? <div className="source-page-state" role="alert"><p>{error}</p><button className="btn" onClick={() => setAttempt(value => value + 1)}>重新加载章节</button></div>
      : !index ? <div className="source-page-state" role="status">正在读取章节目录…</div>
      : !current ? <div className="source-page-state" role="status">此章节暂无可阅读原文页</div>
      : <>
        <div className="chapter-page-jump"><label htmlFor="chapter-page-select">按品种跳转</label>
          <select id="chapter-page-select" value={page} onChange={event => go(index.pages.find(item => item.page === Number(event.target.value)))}>
            {index.pages.map(item => <option key={item.page} value={item.page}>{item.label} · PDF {item.page} 页</option>)}
          </select></div>
        {navigation()}
        <SourcePdfContent key={`${bookId}:${page}`} bookId={bookId} page={current.page} initialMode={current.kind === 'chapter_title' ? 'pdf' : readingMode} onModeChange={current.kind === 'content' ? setReadingMode : undefined} />
        {navigation(true)}
      </>}
  </main>;
}
